'use client';

import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { useQuery } from '@tanstack/react-query';

/**
 * Current version of the app that is running
 */
let version: string | null = null;

/**
 * Default interval time in seconds to check for new version
 * By default, it is set to 60 seconds
 */
const DEFAULT_REFETCH_INTERVAL = 60;

/**
 * Default interval time in seconds to check for new version
 */
const VERSION_UPDATER_REFETCH_INTERVAL_SECONDS =
  process.env.NEXT_PUBLIC_VERSION_UPDATER_REFETCH_INTERVAL_SECONDS;

/**
 * Silently keeps the client on the latest deployed build.
 *
 * When `/api/version` reports a new build we do NOT interrupt the user with a
 * modal. We arm a hard reload and fire it at the next client-side navigation —
 * a provably safe boundary:
 *
 *   - The user is leaving the current page, so any un-guarded in-page form
 *     state is already being discarded by their own navigation (a soft nav
 *     unmounts the page either way). Reloading at that instant loses nothing
 *     the user was keeping.
 *   - Dirty-form guards that register a `beforeunload` handler (the wizard in
 *     `multi-step-form.tsx`, the reimbursement draft hook) still fire on the
 *     hard reload, so genuinely-unsaved work surfaces the native prompt.
 *
 * An idle user who never navigates simply keeps the old build until they next
 * move — no forced reload, no data loss, no dialog. This renders nothing.
 */
export function VersionUpdater(props: { intervalTimeInSecond?: number }) {
  const { data } = useVersionUpdater(props);
  const pathname = usePathname();

  const pendingReload = useRef(false);
  const lastPathname = useRef(pathname);

  // Arm the reload the moment a new build is detected.
  useEffect(() => {
    if (data?.didChange) {
      pendingReload.current = true;
    }
  }, [data?.didChange]);

  // Fire the armed reload on the next navigation (a safe boundary — the user is
  // leaving the current page, so its transient state is no longer at stake).
  useEffect(() => {
    if (pathname === lastPathname.current) {
      return;
    }

    lastPathname.current = pathname;

    if (pendingReload.current) {
      window.location.reload();
    }
  }, [pathname]);

  return null;
}

function useVersionUpdater(props: { intervalTimeInSecond?: number } = {}) {
  const interval = VERSION_UPDATER_REFETCH_INTERVAL_SECONDS
    ? Number(VERSION_UPDATER_REFETCH_INTERVAL_SECONDS)
    : DEFAULT_REFETCH_INTERVAL;

  const refetchInterval = (props.intervalTimeInSecond ?? interval) * 1000;

  // start fetching new version after half of the interval time
  const staleTime = refetchInterval / 2;

  return useQuery({
    queryKey: ['version-updater'],
    staleTime,
    gcTime: refetchInterval,
    refetchIntervalInBackground: true,
    refetchInterval,
    initialData: null,
    queryFn: async () => {
      // Same-origin fetch — must NOT use NEXT_PUBLIC_SITE_URL.
      // When the env var points at a different host (e.g. flow.ballee.co
      // while the user is on new.ballee.app), the absolute URL trips the
      // CSP `connect-src 'self'` directive and the request is blocked.
      const response = await fetch('/api/version');

      const currentVersion = await response.text();
      const oldVersion = version;

      version = currentVersion;

      const didChange = oldVersion !== null && currentVersion !== oldVersion;

      return {
        currentVersion,
        oldVersion,
        didChange,
      };
    },
  });
}
